import type { CSSProperties } from 'react';
import type { ChartPoint } from '../ColumnChart/chart-point.ts';
import { ChartTable } from '../ChartTable/ChartTable.tsx';
import { EmptyState } from '../EmptyState/EmptyState.tsx';
import { Skeleton } from '../Skeleton/Skeleton.tsx';
import styles from './FunnelChart.module.css';

export type FunnelStepShare = { readonly ofFirst: number; readonly fromPrevious: number | null };

export type FunnelChartProps = {
  label: string;
  steps: readonly ChartPoint[];
  describeStep: (share: FunnelStepShare) => string;
  emptyTitle: string;
  loading?: boolean;
};

const ratioOf = (value: number, base: number | undefined): number =>
  base === undefined || base <= 0 ? 0 : Math.max(value, 0) / base;

export const funnelSharesOf = (steps: readonly ChartPoint[]): readonly FunnelStepShare[] => {
  const first = steps[0]?.value;
  return steps.map((step, index) => ({
    ofFirst: ratioOf(step.value, first),
    fromPrevious: index === 0 ? null : ratioOf(step.value, steps[index - 1]?.value),
  }));
};

export const FunnelChart = ({
  label,
  steps,
  describeStep,
  emptyTitle,
  loading = false,
}: FunnelChartProps) => {
  if (loading) return <Skeleton height="14rem" />;
  if ((steps[0]?.value ?? 0) <= 0) return <EmptyState title={emptyTitle} />;
  const shares = funnelSharesOf(steps);
  return (
    <figure className={styles['funnel']}>
      <figcaption className="visually-hidden">{label}</figcaption>
      <ol className={styles['steps']}>
        {steps.map((step, index) => {
          const share = shares[index] ?? { ofFirst: 0, fromPrevious: null };
          return (
            <li key={step.key} className={styles['step']}>
              <span className={styles['name']}>{step.label}</span>
              <span className={styles['value']}>{step.display}</span>
              <span className={styles['track']} aria-hidden="true">
                <span
                  className={styles['fill']}
                  style={{ '--funnel-share': String(share.ofFirst) } as CSSProperties}
                />
              </span>
              <span className={styles['share']}>{describeStep(share)}</span>
            </li>
          );
        })}
      </ol>
      <ChartTable label={label} points={steps} />
    </figure>
  );
};
